/* eslint-disable camelcase -- PostHog's own names (get_distinct_id, family_id, …) */
import posthog from "posthog-js";

import { SCREENS } from "@/navigation/screens";

import type { PosthogSettings } from "./posthog-config";
import { buildPosthogConfig } from "./posthog-config";

/**
 * The calls this app makes into posthog-js, behind one object so tests can
 * pass a fake.
 */
export type PosthogClient = {
  init: (key: string, config: ReturnType<typeof buildPosthogConfig>) => void;
  capture: (name: string) => void;
  identify: (userId: string, properties: Record<string, unknown>) => void;
  distinctId: () => string;
  isIdentified: () => boolean;
  reset: () => void;
  startRecording: () => void;
  stopRecording: () => void;
  currentLocation: () => string;
};

export type PosthogSession =
  | { userId?: string | null; familyId?: string | null }
  | null
  | undefined;

const defaultClient: PosthogClient = {
  init: (key, config) => {
    posthog.init(key, config);
  },
  capture: (name) => {
    posthog.capture(name);
  },
  identify: (userId, properties) => posthog.identify(userId, properties),
  distinctId: () => posthog.get_distinct_id(),
  // "identified" after identify(), "anonymous" after reset() or before sign-in.
  isIdentified: () => posthog.get_property("$user_state") === "identified",
  reset: () => posthog.reset(),
  startRecording: () => posthog.startSessionRecording(),
  stopRecording: () => posthog.stopSessionRecording(),
  // Search included: wizard pages differ only by `?page=N`.
  currentLocation: () => window.location.pathname + window.location.search,
};

/**
 * The Heyflow onboarding screens. PostHog sends events and records replay on
 * these routes only; every other v1 screen sends nothing.
 */
const ONBOARDING_ROUTES: ReadonlySet<string> = new Set([
  SCREENS.SetPassword.name,
  SCREENS.Welcome.name,
  SCREENS.WizardIntro.name,
  SCREENS.WizardSection.name,
  SCREENS.WizardCompleted.name,
  SCREENS.WizardNotFound.name,
]);

// Analytics must never take down navigation or sign-out. Logged, not swallowed.
function safely(run: () => void): boolean {
  try {
    run();
    return true;
  } catch (error) {
    console.error("[posthog] call failed:", error);
    return false;
  }
}

let enabled = false;
let client: PosthogClient = defaultClient;
let inScope = false;
let recording = false;
let lastPageview: string | null = null;

/** Starts PostHog where this environment has a project. Call once, at boot. */
export function initPosthog(
  settings?: PosthogSettings,
  withClient: PosthogClient = defaultClient,
): void {
  if (!settings?.key || !settings.host) return;
  client = withClient;
  enabled = safely(() =>
    client.init(
      settings.key,
      buildPosthogConfig(settings.host, () => inScope),
    ),
  );
}

export function isPosthogEnabled(): boolean {
  return enabled;
}

function identify(session: PosthogSession): void {
  const userId = session?.userId;
  if (!userId) return;
  safely(() => {
    if (client.distinctId() === userId) return;
    // Another family is still identified on this browser: the bare logout()
    // on set-password does not go through posthogReset. Start from a fresh id
    // rather than merging two families.
    if (client.isIdentified()) client.reset();
    client.identify(userId, { family_id: session?.familyId ?? null });
  });
}

/**
 * Call once the URL has settled after a navigation (navigation/index.tsx).
 * On an onboarding route: identify a signed-in family, record, and send one
 * $pageview per URL. Anywhere else: stop recording; before_send drops every
 * other event. Identity is set here, not at login, because the Heyflow login
 * happens on /token, outside onboarding.
 */
export function posthogRouteSettled(
  routeName: string | undefined,
  session: PosthogSession,
): void {
  if (!enabled) return;
  inScope = routeName !== undefined && ONBOARDING_ROUTES.has(routeName);
  if (!inScope) {
    if (recording) safely(() => client.stopRecording());
    recording = false;
    lastPageview = null;
    return;
  }
  // Set-password is pre-login: the Heyflow lead is still anonymous there, and
  // a live session on that screen belongs to whoever else is signed in on
  // this browser. Identifying it would merge the lead into that family.
  if (routeName !== SCREENS.SetPassword.name) identify(session);
  if (!recording) recording = safely(() => client.startRecording());
  safely(() => {
    const location = client.currentLocation();
    if (location === lastPageview) return;
    lastPageview = location;
    client.capture("$pageview");
  });
}

/**
 * Forgets the signed-in family on sign-out. Only resets an identified person:
 * resetting an anonymous visitor would mint a new id on every signed-out load.
 */
export function posthogReset(): void {
  if (!enabled) return;
  safely(() => {
    if (client.isIdentified()) client.reset();
  });
}
